/**
 * KAILAS Size Chart Enhanced Interactions
 * Provides accordion functionality and smooth animations
 * Also handles modal size chart table generation
 */

(function() {
  'use strict';

  function displayValue(value) {
    if (value === undefined || value === null) return '-';

    const normalizedValue = String(value).trim();
    return normalizedValue === '' || normalizedValue.toLowerCase() === 'nan' ? '-' : normalizedValue;
  }

  function parseSizeNumber(value) {
    const number = parseFloat(value);
    return Number.isFinite(number) ? number : null;
  }

  function buildRangeCell(value, dataAttributePrefix, valueClass, convertedClass) {
    const display = displayValue(value);
    const rangeParts = display === '-' ? [] : display.split('-');
    const min = parseSizeNumber(rangeParts[0]);
    const max = rangeParts.length > 1 ? parseSizeNumber(rangeParts[1]) : min;

    if (min === null) return '<td>-</td>';

    return `<td data-${dataAttributePrefix}-min="${min}" data-${dataAttributePrefix}-max="${max}">
      <span class="${valueClass}">${display}</span>
      <span class="${convertedClass}" style="display: none;"></span>
    </td>`;
  }

  function buildMeasurementCell(value) {
    const display = displayValue(value);
    const cmValue = parseSizeNumber(display);

    if (cmValue === null) return '<td>-</td>';

    return `<td data-cm-value="${cmValue}">
      <span class="size-chart-measurement-cm">${display}</span>
      <span class="size-chart-measurement-inch" style="display: none;"></span>
    </td>`;
  }

  // Initialize modal size chart tables
  function initModalSizeChart() {
    const tableWrappers = document.querySelectorAll('.size-chart-table-wrapper[data-size-chart]');
    
    tableWrappers.forEach(wrapper => {
      const chartType = wrapper.getAttribute('data-chart-type');
      const sizeData = wrapper.getAttribute('data-size-chart');
      const tbody = wrapper.querySelector('.size-chart-tbody');
      
      if (!tbody || !sizeData) return;
      
      // Clear existing rows if any
      tbody.innerHTML = '';
      
      if (chartType === 'men_shoes') {
        parseMenShoesData(sizeData, tbody, wrapper);
      } else if (chartType === 'women_shoes') {
        parseWomenShoesData(sizeData, tbody, wrapper);
      } else if (chartType === 'gloves') {
        parseGlovesData(sizeData, tbody, wrapper);
      } else if (chartType === 'socks') {
        parseSocksData(sizeData, tbody, wrapper);
      } else if (chartType === 'vest_packs') {
        parseVestPacksData(sizeData, tbody, wrapper);
      } else if (chartType === 'waist_packs') {
        parseVestPacksData(sizeData, tbody, wrapper);
      } else if (chartType === 'tops') {
        parseTopsData(sizeData, tbody, wrapper);
      } else if (chartType === 'bottoms') {
        parseBottomsData(sizeData, tbody, wrapper);
      } else if (chartType === 'sand_gaiters') {
        parseSandGaitersData(sizeData, tbody, wrapper);
      }
    });
    
    // Initialize unit toggle
    initUnitToggle();
  }
  
  // Parse men shoes data:
  // EUR,US,Foot Length Range
  // EUR,US,UK,Foot Length Range
  // EUR,US,Foot Length Range,Standard Width,Wide Width
  // EUR,US,UK,Foot Length Range,Standard Width,Wide Width
  function parseMenShoesData(data, tbody, wrapper) { 
    const lines = data.trim().split('\n');
    const rows = [];
    
    lines.forEach(line => {
      const parts = line.trim().split(',');
      if (parts.length >= 3) {
        const eur = parts[0].trim();
        const us = parts[1].trim();
        let ukSize, footLength, standardWidth = '', wideWidth = '';
        
        if (parts.length >= 6) {
          // Format: EUR,US,UK,Foot Length Range,Standard Width,Wide Width
          ukSize = parts[2].trim();
          footLength = parts[3].trim();
          standardWidth = parts[4].trim();
          wideWidth = parts[5].trim();
        } else if (parts.length === 5) {
          // Format: EUR,US,Foot Length Range,Standard Width,Wide Width
          footLength = parts[2].trim();
          standardWidth = parts[3].trim();
          wideWidth = parts[4].trim();
          const usNum = parseFloat(us);
          ukSize = isNaN(usNum) ? '' : (usNum - 0.5).toString();
        } else if (parts.length >= 4) {
          // Format: EUR,US,UK,Foot Length Range
          ukSize = parts[2].trim();
          footLength = parts[3].trim();
        } else {
          // Format: EUR,US,Foot Length Range - calculate UK size
          footLength = parts[2].trim();
          const usNum = parseFloat(us);
          ukSize = isNaN(usNum) ? '' : (usNum - 0.5).toString();
        }
        
        rows.push({
          eur: eur,
          us: us,
          uk: ukSize,
          footLength: footLength,
          standardWidth: standardWidth,
          wideWidth: wideWidth
        });
      }
    });
    
    // Check if any row has standard/wide width data
    const hasStandardWidth = rows.some(row => row.standardWidth && row.standardWidth !== '');
    const hasWideWidth = rows.some(row => row.wideWidth && row.wideWidth !== '');

    // Generate table rows
    rows.forEach(row => {
      const tr = document.createElement('tr');
      
      // Parse standard width and wide width values for unit toggle
      const swParts = row.standardWidth ? row.standardWidth.split('-') : [];
      const swMin = swParts.length > 0 ? swParts[0].trim() : '';
      const swMax = swParts.length > 1 ? swParts[1].trim() : swMin;
      const wwParts = row.wideWidth ? row.wideWidth.split('-') : [];
      const wwMin = wwParts.length > 0 ? wwParts[0].trim() : '';
      const wwMax = wwParts.length > 1 ? wwParts[1].trim() : wwMin;

      // Build width cell HTML with unit toggle support
      const swHtml = displayValue(row.standardWidth) !== '-'
        ? `<td class="size-chart-standard-width-col" data-mm-min="${swMin}" data-mm-max="${swMax}">
            <span class="size-chart-width-mm">${displayValue(row.standardWidth)}</span>
            <span class="size-chart-width-inch" style="display: none;"></span>
          </td>`
        : '<td class="size-chart-standard-width-col">-</td>';
      const wwHtml = displayValue(row.wideWidth) !== '-'
        ? `<td class="size-chart-wide-width-col" data-mm-min="${wwMin}" data-mm-max="${wwMax}">
            <span class="size-chart-width-mm">${displayValue(row.wideWidth)}</span>
            <span class="size-chart-width-inch" style="display: none;"></span>
          </td>`
        : '<td class="size-chart-wide-width-col">-</td>';
      
      tr.innerHTML = `
        <td>${displayValue(row.eur)}</td>
        <td class="size-chart-uk-column">${displayValue(row.uk)}</td>
        <td>${displayValue(row.us)}</td>
        ${buildRangeCell(row.footLength, 'mm', 'size-chart-foot-length-mm', 'size-chart-foot-length-inch')}
        ${swHtml}
        ${wwHtml}
      `;
      
      tbody.appendChild(tr);
    });

    // Hide standard/wide width columns if no data exists
    const table = wrapper.querySelector('.size-chart-table--men-shoes');
    if (table) {
      if (!hasStandardWidth) {
        const thStandard = table.querySelector('thead th[data-mm-text="STANDARD WIDTH(MM)"]');
        if (thStandard) thStandard.style.display = 'none';
        table.querySelectorAll('tbody .size-chart-standard-width-col').forEach(td => td.style.display = 'none');
      }
      if (!hasWideWidth) {
        const thWide = table.querySelector('thead th[data-mm-text="WIDE WIDTH(MM)"]');
        if (thWide) thWide.style.display = 'none';
        table.querySelectorAll('tbody .size-chart-wide-width-col').forEach(td => td.style.display = 'none');
      }
    }
    
    // Default display: MM
    updateUnitDisplay(wrapper, 'mm');
  }
  
  // Parse women shoes data: EUR,US,Foot Length Range or EUR,US,UK,Foot Length Range
  function parseWomenShoesData(data, tbody, wrapper) {
    const lines = data.trim().split('\n');
    const rows = [];
    
    lines.forEach(line => {
      const parts = line.trim().split(',');
      if (parts.length >= 3) {
        const eur = parts[0].trim();
        const us = parts[1].trim();
        let ukSize, footLength;
        
        // Check if data format is EUR,US,UK,Foot Length (4 columns) or EUR,US,Foot Length (3 columns)
        if (parts.length >= 4) {
          // Format: EUR,US,UK,Foot Length Range
          ukSize = parts[2].trim();
          footLength = parts[3].trim();
        } else {
          // Format: EUR,US,Foot Length Range - calculate UK size
          footLength = parts[2].trim();
          const usNum = parseFloat(us);
          ukSize = isNaN(usNum) ? '' : (usNum - 0.5).toString();
        }
        
        rows.push({
          eur: eur,
          us: us,
          uk: ukSize,
          footLength: footLength
        });
      }
    });
    
    // Generate table rows
    rows.forEach(row => {
      const tr = document.createElement('tr');
      
      tr.innerHTML = `
        <td>${displayValue(row.eur)}</td>
        <td class="size-chart-uk-column">${displayValue(row.uk)}</td>
        <td>${displayValue(row.us)}</td>
        ${buildRangeCell(row.footLength, 'mm', 'size-chart-foot-length-mm', 'size-chart-foot-length-inch')}
      `;
      
      tbody.appendChild(tr);
    });
    
    // Default display: MM
    updateUnitDisplay(wrapper, 'mm');
  }
  
  // Parse shoes data: EUR,US Men,US Women,Foot Length Range
  function parseShoesData(data, tbody, wrapper) {
    const lines = data.trim().split('\n');
    const rows = [];
    
    lines.forEach(line => {
      const parts = line.trim().split(',');
      if (parts.length >= 4) {
        const eur = parts[0].trim();
        const usMen = parts[1].trim();
        const usWomen = parts[2].trim();
        const footLength = parts[3].trim(); // Format: "245-248" or "245"
        
        // Calculate UK size (typically UK = US - 0.5)
        const usMenNum = parseFloat(usMen);
        const ukSize = isNaN(usMenNum) ? '' : (usMenNum - 0.5).toString();
        
        rows.push({
          eur: eur,
          us: usMen,
          uk: ukSize,
          footLength: footLength
        });
      }
    });
    
    // Generate table rows
    rows.forEach(row => {
      const tr = document.createElement('tr');
      
      tr.innerHTML = `
        <td>${displayValue(row.eur)}</td>
        <td class="size-chart-uk-column">${displayValue(row.uk)}</td>
        <td>${displayValue(row.us)}</td>
        ${buildRangeCell(row.footLength, 'mm', 'size-chart-foot-length-mm', 'size-chart-foot-length-inch')}
      `;
      
      tbody.appendChild(tr);
    });
    
    // Convert MM to INCH for initial display
    updateUnitDisplay(wrapper, 'mm');
  }
  
  // Parse gloves data
  // Format: Category,Measurement Type,S,M,L
  // Example: Women's,Palm Circumference,S,165-180,
  function parseGlovesData(data, tbody, wrapper) {
    const lines = data.trim().split('\n');
    const categoryData = {};
    
    // Parse all lines and group by category
    lines.forEach(line => {
      const parts = line.trim().split(',');
      if (parts.length >= 5) {
        const category = parts[0].trim();
        const measurementType = parts[1].trim();
        const s = parts[2].trim();
        const m = parts[3].trim();
        const l = parts[4].trim();
        
        if (!categoryData[category]) {
          categoryData[category] = {
            palmCircumference: { s: '', m: '', l: '' },
            middleFingerLength: { s: '', m: '', l: '' }
          };
        }
        
        if (measurementType === 'Palm Circumference') {
          categoryData[category].palmCircumference.s = displayValue(s);
          categoryData[category].palmCircumference.m = displayValue(m);
          categoryData[category].palmCircumference.l = displayValue(l);
        } else if (measurementType === 'Middle Finger Length') {
          categoryData[category].middleFingerLength.s = displayValue(s);
          categoryData[category].middleFingerLength.m = displayValue(m);
          categoryData[category].middleFingerLength.l = displayValue(l);
        }
      }
    });
    
    // Define category order
    const categoryOrder = ["Women's", "Men's", "Unisex"];
    
    // Generate table rows for each category
    categoryOrder.forEach(category => {
      if (categoryData[category]) {
        const data = categoryData[category];
        
        // Create row for Palm Circumference
        const tr1 = document.createElement('tr');
        tr1.innerHTML = `
          <td rowspan="2" class="size-sticky-left">${displayValue(category).toUpperCase()}</td>
          <td>PALM CIRCUMFERENCE</td>
          <td>${data.palmCircumference.s}</td>
          <td>${data.palmCircumference.m}</td>
          <td>${data.palmCircumference.l}</td>
        `;
        tbody.appendChild(tr1);
        
        // Create row for Middle Finger Length
        const tr2 = document.createElement('tr');
        tr2.innerHTML = `
          <td>MIDDLE FINGER LENGTH</td>
          <td>${data.middleFingerLength.s}</td>
          <td>${data.middleFingerLength.m}</td>
          <td>${data.middleFingerLength.l}</td>
        `;
        tbody.appendChild(tr2);
      }
    });
  }
  
  // Parse socks data
  // Format: Category,Sock Size,Foot Length Range,EU Shoe Size
  // Example: Unisex,S,240-260,37-39
  function parseSocksData(data, tbody, wrapper) {
    const lines = data.trim().split('\n');
    const categoryData = {};
    
    // Parse all lines and group by category
    lines.forEach(line => {
      const parts = line.trim().split(',');
      if (parts.length >= 4) {
        const category = parts[0].trim();
        const sockSize = parts[1].trim();
        const footLength = parts[2].trim();
        const euShoeSize = parts[3].trim();
        
        if (!categoryData[category]) {
          categoryData[category] = [];
        }
        
        categoryData[category].push({
          sockSize: displayValue(sockSize),
          footLength: displayValue(footLength),
          euShoeSize: displayValue(euShoeSize)
        });
      }
    });
    
    // Define category order
    const categoryOrder = ["Unisex", "Men's", "Women's"];
    
    // Generate table rows for each category
    categoryOrder.forEach(category => {
      if (categoryData[category]) {
        const rows = categoryData[category];
        
        rows.forEach((row, index) => {
          const tr = document.createElement('tr');
          
          // First row of each category should have rowspan
          if (index === 0) {
            tr.innerHTML = `
              <td rowspan="${rows.length}" class="size-sticky-left">${displayValue(category).toUpperCase()}</td>
              <td>${displayValue(row.sockSize)}</td>
              <td>${displayValue(row.footLength)}</td>
              <td>${displayValue(row.euShoeSize)}</td>
            `;
          } else {
            tr.innerHTML = `
              <td>${displayValue(row.sockSize)}</td>
              <td>${displayValue(row.footLength)}</td>
              <td>${displayValue(row.euShoeSize)}</td>
            `;
          }
          
          tbody.appendChild(tr);
        });
      }
    });
  }
  
  // Parse vest packs data
  // Format: Size,Circumference (CM)
  // Example: XS,62-69
  function parseVestPacksData(data, tbody, wrapper) {
    const lines = data.trim().split('\n');
    const rows = [];
    
    lines.forEach(line => {
      const parts = line.trim().split(',');
      if (parts.length >= 2) {
        const size = parts[0].trim();
        const circumference = parts[1].trim(); // Format: "62-69" or "62"
        
        rows.push({
          size: size,
          circumference: circumference
        });
      }
    });
    
    // Generate table rows
    rows.forEach(row => {
      const tr = document.createElement('tr');
      
      tr.innerHTML = `
        <td>${displayValue(row.size)}</td>
        ${buildRangeCell(row.circumference, 'cm', 'size-chart-circumference-cm', 'size-chart-circumference-inch')}
      `;
      
      tbody.appendChild(tr);
    });
    
    // Convert CM to INCH for initial display
    updateUnitDisplay(wrapper, 'cm');
  }
  
  // Parse tops data
  // Format: Size,Specification,Length,Chest Circumference,Shoulder Width,Sleeve Length (CM)
  // Example: XXS,160/84A,67.5,108,43.9,61.5
  function parseTopsData(data, tbody, wrapper) {
    const lines = data.trim().split('\n');
    const rows = [];
    
    lines.forEach(line => {
      const parts = line.trim().split(',');
      if (parts.length >= 6) {
        const size = parts[0].trim();
        const specification = parts[1].trim();
        const length = parts[2].trim();
        const chestCircumference = parts[3].trim();
        const shoulderWidth = parts[4].trim();
        const sleeveLength = parts[5].trim();
        
        rows.push({
          size: size,
          specification: specification,
          length: length,
          chestCircumference: chestCircumference,
          shoulderWidth: shoulderWidth,
          sleeveLength: sleeveLength
        });
      }
    });
    
    // Generate table rows
    rows.forEach(row => {
      const tr = document.createElement('tr');
      
      tr.innerHTML = `
        <td>${displayValue(row.size)}</td>
        <td>${displayValue(row.specification)}</td>
        ${buildMeasurementCell(row.length)}
        ${buildMeasurementCell(row.chestCircumference)}
        ${buildMeasurementCell(row.shoulderWidth)}
        ${buildMeasurementCell(row.sleeveLength)}
      `;
      
      tbody.appendChild(tr);
    });
    
    // Convert CM to INCH for initial display
    updateUnitDisplay(wrapper, 'cm');
  }
  
  // Parse sand gaiters data
  // Format: Category,S,M,L
  // Example:
  //   Shoe Size,EUR 36-39.5,EUR 40-43.5,EUR 44-47
  //   Calf Circumference (above Achilles),>19cm ≤21cm,>22cm ≤24cm,>25cm ≤27cm
  function parseSandGaitersData(data, tbody, wrapper) {
    const lines = data.trim().split('\n');
    
    lines.forEach(line => {
      const parts = line.trim().split(',');
      if (parts.length >= 4) {
        const category = parts[0].trim();
        const s = parts[1].trim();
        const m = parts[2].trim();
        const l = parts[3].trim();
        
        const tr = document.createElement('tr');
        tr.innerHTML = `
          <td class="size-chart-category-cell">${displayValue(category)}</td>
          <td>${displayValue(s)}</td>
          <td>${displayValue(m)}</td>
          <td>${displayValue(l)}</td>
        `;
        tbody.appendChild(tr);
      }
    });
  }

  // Parse bottoms data
  // Format: Size,Specification,Waist,Stretched Waist,Thigh,Hip,Length (CM)
  // Example: 30,160/84A,73,82,62.2,100,99.5
  function parseBottomsData(data, tbody, wrapper) {
    const lines = data.trim().split('\n');
    const rows = [];
    
    lines.forEach(line => {
      const parts = line.trim().split(',');
      if (parts.length >= 7) {
        const size = parts[0].trim();
        const specification = parts[1].trim();
        const waist = parts[2].trim();
        const stretchedWaist = parts[3].trim();
        const thigh = parts[4].trim();
        const hip = parts[5].trim();
        const length = parts[6].trim();
        
        rows.push({
          size: size,
          specification: specification,
          waist: waist,
          stretchedWaist: stretchedWaist,
          thigh: thigh,
          hip: hip,
          length: length
        });
      }
    });
    
    // Generate table rows
    rows.forEach(row => {
      const tr = document.createElement('tr');
      
      tr.innerHTML = `
        <td>${displayValue(row.size)}</td>
        <td>${displayValue(row.specification)}</td>
        ${buildMeasurementCell(row.waist)}
        ${buildMeasurementCell(row.stretchedWaist)}
        ${buildMeasurementCell(row.thigh)}
        ${buildMeasurementCell(row.hip)}
        ${buildMeasurementCell(row.length)}
      `;
      
      tbody.appendChild(tr);
    });
    
    // Convert CM to INCH for initial display
    updateUnitDisplay(wrapper, 'cm');
  }
  
  // Initialize unit toggle functionality
  function initUnitToggle() {
    const unitButtons = document.querySelectorAll('.size-chart-unit-btn');
    
    unitButtons.forEach(btn => {
      btn.addEventListener('click', function() {
        const unit = this.getAttribute('data-unit');
        const wrapper = this.closest('.size-chart-table-wrapper');
        
        // Update active state
        wrapper.querySelectorAll('.size-chart-unit-btn').forEach(b => {
          b.classList.remove('active');
        });
        this.classList.add('active');
        
        // Update display
        updateUnitDisplay(wrapper, unit);
      });
    });
  }
  
  // Update unit display (MM/INCH for shoes, CM/INCH for vest_packs, tops, and bottoms)
  function updateUnitDisplay(wrapper, unit) {
    const chartType = wrapper.getAttribute('data-chart-type');
    
    // Handle men_shoes, women_shoes, and shoes (MM/INCH)
    const footLengthCells = wrapper.querySelectorAll('td[data-mm-min]');
    footLengthCells.forEach(cell => {
      const mmMin = parseFloat(cell.getAttribute('data-mm-min'));
      const mmMax = parseFloat(cell.getAttribute('data-mm-max'));
      
      const mmSpan = cell.querySelector('.size-chart-foot-length-mm');
      const inchSpan = cell.querySelector('.size-chart-foot-length-inch');
      
      if (!mmSpan || !inchSpan) return;
      
      if (unit === 'mm') {
        const range = mmMax && mmMax !== mmMin ? `${mmMin}-${mmMax}` : mmMin.toString();
        mmSpan.textContent = range;
        mmSpan.style.display = '';
        inchSpan.style.display = 'none';
      } else {
        const inchMin = (mmMin / 25.4).toFixed(1);
        const inchMax = mmMax && mmMax !== mmMin ? (mmMax / 25.4).toFixed(1) : inchMin;
        const range = mmMax && mmMax !== mmMin ? `${inchMin}-${inchMax}` : inchMin;
        inchSpan.textContent = range;
        mmSpan.style.display = 'none';
        inchSpan.style.display = '';
      }
    });
    
    // Handle men_shoes width columns (MM/INCH)
    const widthCells = wrapper.querySelectorAll('td.size-chart-standard-width-col[data-mm-min], td.size-chart-wide-width-col[data-mm-min]');
    widthCells.forEach(cell => {
      const mmMin = parseFloat(cell.getAttribute('data-mm-min'));
      const mmMax = parseFloat(cell.getAttribute('data-mm-max'));

      if (isNaN(mmMin)) return;

      const mmSpan = cell.querySelector('.size-chart-width-mm');
      const inchSpan = cell.querySelector('.size-chart-width-inch');

      if (!mmSpan || !inchSpan) return;

      if (unit === 'mm') {
        const range = mmMax && mmMax !== mmMin ? `${mmMin}-${mmMax}` : mmMin.toString();
        mmSpan.textContent = range;
        mmSpan.style.display = '';
        inchSpan.style.display = 'none';
      } else {
        const inchMin = (mmMin / 25.4).toFixed(1);
        const inchMax = mmMax && mmMax !== mmMin ? (mmMax / 25.4).toFixed(1) : inchMin;
        const range = mmMax && mmMax !== mmMin ? `${inchMin}-${inchMax}` : inchMin;
        inchSpan.textContent = range;
        mmSpan.style.display = 'none';
        inchSpan.style.display = '';
      }
    });

    // Handle vest_packs (CM/INCH)
    const circumferenceCells = wrapper.querySelectorAll('td[data-cm-min]');
    circumferenceCells.forEach(cell => {
      const cmMin = parseFloat(cell.getAttribute('data-cm-min'));
      const cmMax = parseFloat(cell.getAttribute('data-cm-max'));
      
      const cmSpan = cell.querySelector('.size-chart-circumference-cm');
      const inchSpan = cell.querySelector('.size-chart-circumference-inch');
      
      if (unit === 'cm') {
        const range = cmMax && cmMax !== cmMin ? `${cmMin}-${cmMax}` : cmMin.toString();
        cmSpan.textContent = range;
        cmSpan.style.display = '';
        if (inchSpan) inchSpan.style.display = 'none';
      } else {
        const inchMin = (cmMin / 2.54).toFixed(1);
        const inchMax = cmMax && cmMax !== cmMin ? (cmMax / 2.54).toFixed(1) : inchMin;
        const range = cmMax && cmMax !== cmMin ? `${inchMin}-${inchMax}` : inchMin;
        if (inchSpan) {
          inchSpan.textContent = range;
          cmSpan.style.display = 'none';
          inchSpan.style.display = '';
        }
      }
    });
    
    // Handle tops (CM/INCH) - multiple measurement columns
    const topsMeasurementCells = wrapper.querySelectorAll('td[data-cm-value]');
    topsMeasurementCells.forEach(cell => {
      const cmValue = parseFloat(cell.getAttribute('data-cm-value'));
      
      const cmSpan = cell.querySelector('.size-chart-measurement-cm');
      const inchSpan = cell.querySelector('.size-chart-measurement-inch');
      
      if (unit === 'cm') {
        cmSpan.textContent = cmValue.toString();
        cmSpan.style.display = '';
        if (inchSpan) inchSpan.style.display = 'none';
      } else {
        const inchValue = (cmValue / 2.54).toFixed(1);
        if (inchSpan) {
          inchSpan.textContent = inchValue;
          cmSpan.style.display = 'none';
          inchSpan.style.display = '';
        }
      }
    });

    // Update column headers that carry a unit label
    const unitHeaders = wrapper.querySelectorAll('th[data-mm-text]');
    unitHeaders.forEach(th => {
      if (unit === 'mm' || unit === 'cm') {
        th.textContent = th.getAttribute('data-mm-text');
      } else {
        th.textContent = th.getAttribute('data-inch-text');
      }
    });
  }

  // Initialize size chart functionality
  function initSizeChart() {
    const sizeChartContainer = document.querySelector('.size-chart-container');
    if (!sizeChartContainer) return;

    // Accordion toggle functionality
    const sectionHeaders = document.querySelectorAll('.size-section-header');
    
    sectionHeaders.forEach(header => {
      header.addEventListener('click', function(e) {
        e.preventDefault();
        const section = this.closest('.size-section');
        const allSections = document.querySelectorAll('.size-section');
        
        // Toggle current section
        section.classList.toggle('active');
        
        // Optional: Close other sections (uncomment for single-open behavior)
        // allSections.forEach(s => {
        //   if (s !== section) {
        //     s.classList.remove('active'); 
        //   }
        // });
      });
    });

    // Add keyboard accessibility
    sectionHeaders.forEach(header => {
      header.setAttribute('role', 'button');
      header.setAttribute('tabindex', '0');
      
      header.addEventListener('keypress', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.click();
        }
      });
    });

    // Add ARIA attributes for accessibility
    const sections = document.querySelectorAll('.size-section');
    sections.forEach((section, index) => {
      const header = section.querySelector('.size-section-header');
      const content = section.querySelector('.size-section-content');
      
      const headerId = `size-section-header-${index}`;
      const contentId = `size-section-content-${index}`;
      
      header.setAttribute('id', headerId);
      header.setAttribute('aria-controls', contentId);
      
      content.setAttribute('id', contentId);
      content.setAttribute('role', 'region');
      content.setAttribute('aria-labelledby', headerId);
      
      // Update aria-expanded based on active state
      updateAriaExpanded(section);
    });

    // Update ARIA expanded state
    function updateAriaExpanded(section) {
      const header = section.querySelector('.size-section-header');
      const isActive = section.classList.contains('active');
      header.setAttribute('aria-expanded', isActive.toString());
    }

    // Observe class changes to update ARIA
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'class') {
          const section = mutation.target;
          updateAriaExpanded(section);
        }
      });
    });

    sections.forEach(section => {
      observer.observe(section, { attributes: true });
    });

    // Smooth scroll to section if hash in URL
    const hash = window.location.hash;
    if (hash) {
      const targetSection = document.querySelector(hash);
      if (targetSection && targetSection.classList.contains('size-section')) {
        setTimeout(() => {
          targetSection.classList.add('active');
          targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }

    // Table responsive enhancement
    enhanceTableResponsiveness();

    // Add print functionality
    // addPrintButton();
  }

  // Enhance table responsiveness
  function enhanceTableResponsiveness() {
    const tables = document.querySelectorAll('.size-table');
    
    tables.forEach(table => {
      // Add touch scroll hint for mobile
      const wrapper = table.closest('.size-table-wrapper');
      if (wrapper && isMobileDevice()) {
        wrapper.style.cursor = 'grab';
        
        let isScrolling = false;
        wrapper.addEventListener('touchstart', () => {
          isScrolling = true;
          wrapper.style.cursor = 'grabbing';
        });
        
        wrapper.addEventListener('touchend', () => {
          isScrolling = false;
          wrapper.style.cursor = 'grab';
        });
      }
    });
  }

  // Check if device is mobile
  function isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  // Add print functionality
  function addPrintButton() {
    const header = document.querySelector('.size-chart-header');
    if (!header) return;

    const printBtn = document.createElement('button');
    printBtn.className = 'size-chart-print-btn';
    printBtn.innerHTML = '🖨️ Print Size Guide';
    printBtn.style.cssText = `
      margin: 20px auto;
      padding: 12px 24px;
      background: #2c3e50;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
      transition: background 0.3s ease;
      display: block;
    `;

    printBtn.addEventListener('mouseenter', () => {
      printBtn.style.background = '#34495e';
    });

    printBtn.addEventListener('mouseleave', () => {
      printBtn.style.background = '#2c3e50';
    });

    printBtn.addEventListener('click', () => {
      // Open all sections before printing
      document.querySelectorAll('.size-section').forEach(section => {
        section.classList.add('active');
      });

      setTimeout(() => {
        window.print();
      }, 300);
    });

    header.appendChild(printBtn);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initSizeChart();
      initModalSizeChart();
    });
  } else {
    initSizeChart();
    initModalSizeChart();
  }

  // Re-initialize for AJAX-loaded content (Shopify theme extensions)
  document.addEventListener('shopify:section:load', function() {
    initSizeChart();
    initModalSizeChart();
  });
  
  // Initialize modal size chart when modal opens
  document.addEventListener('shopify:block:select', initModalSizeChart);
  
  // Initialize when modals are opened
  document.addEventListener('click', function(e) {
    if (e.target.closest('[aria-controls]')) {
      setTimeout(initModalSizeChart, 100);
    }
  });
})();
